// src/components/configuration/sections/ReferralSettings.jsx
import React, { useState, useEffect } from 'react';
import { Gift, DollarSign, Link, AlertCircle } from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Spinner } from '../../ui/spinner';

// Hooks
import { useSettings } from '../../../hooks/useSettings';
import { useWidgets } from '../../../hooks/useWidgets';

/**
 * Referral Program Settings Component
 *
 * Allows businesses to configure their referral program:
 * - Enable/disable the program
 * - Select which widget referral links point to
 * - Configure reward settings (description, value, type)
 */
const ReferralSettings = () => {
  const {
    businessSettings,
    loading,
    savingSettings,
    updateSettings
  } = useSettings();

  const {
    widgets,
    widgetsLoading,
    loadWidgets
  } = useWidgets();

  // Local state for form
  const [programEnabled, setProgramEnabled] = useState(false);
  const [referralWidgetId, setReferralWidgetId] = useState('');
  const [rewardValue, setRewardValue] = useState('');
  const [rewardType, setRewardType] = useState('credit');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Filter widgets to only show active booking widgets
  const bookingWidgets = widgets?.filter(w =>
    w.widget_type === 'booking' && w.is_active === true
  ) || [];

  // Load widgets on mount
  useEffect(() => {
    loadWidgets();
  }, []);

  // Initialize form from business settings
  useEffect(() => {
    if (businessSettings) {
      setProgramEnabled(businessSettings.referral_program_enabled || false);
      setReferralWidgetId(businessSettings.referral_widget_id || '');
      setRewardValue(businessSettings.referral_reward_value || '');
      setRewardType(businessSettings.referral_reward_type || 'credit');
    }
  }, [businessSettings]);

  // Track changes
  useEffect(() => {
    if (!businessSettings) return;

    const changed =
      programEnabled !== (businessSettings.referral_program_enabled || false) ||
      referralWidgetId !== (businessSettings.referral_widget_id || '') ||
      rewardValue !== (businessSettings.referral_reward_value || '') ||
      rewardType !== (businessSettings.referral_reward_type || 'credit');

    setHasChanges(changed);
    setSaveSuccess(false);
  }, [programEnabled, referralWidgetId, rewardValue, rewardType, businessSettings]);

  // Handle save
  const handleSave = () => {
    updateSettings({
      referral_program_enabled: programEnabled,
      referral_widget_id: referralWidgetId || null,
      referral_reward_value: rewardValue ? parseFloat(rewardValue) : null,
      referral_reward_type: rewardType
    });
    setSaveSuccess(true);
    setHasChanges(false);
  };

  // Loading state
  if (loading && !businessSettings) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toggle */}
      <div className="border border-border rounded-lg bg-muted/20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Gift className="h-5 w-5 text-primary" />
                Referral Program
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Reward customers for referring new clients to your business
              </p>
            </div>
            <Switch
              checked={programEnabled}
              onCheckedChange={setProgramEnabled}
            />
          </div>
        </div>
      </div>

      {/* Program Configuration - Only show when enabled */}
      {programEnabled && (
        <>
          {/* Referral Widget Selection */}
          <div className="border border-border rounded-lg bg-muted/20">
            <div className="px-6 py-4 border-b border-border">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Link className="h-4 w-4" />
                Referral Link Destination
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Select which booking widget referral links will direct new customers to
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="referral-widget">Booking Widget</Label>
                <Select
                  value={referralWidgetId}
                  onValueChange={setReferralWidgetId}
                >
                  <SelectTrigger id="referral-widget">
                    <SelectValue placeholder="Select a widget..." />
                  </SelectTrigger>
                  <SelectContent>
                    {widgetsLoading ? (
                      <div className="p-2 text-center">
                        <Spinner className="size-4 mx-auto" />
                      </div>
                    ) : bookingWidgets.length === 0 ? (
                      <div className="p-2 text-center text-muted-foreground text-sm">
                        No active booking widgets found
                      </div>
                    ) : (
                      bookingWidgets.map(widget => (
                        <SelectItem key={widget.id} value={widget.id}>
                          {widget.widget_name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Referral links will include a tracking code (e.g., ?ref=ABC123)
                </p>
              </div>

              {!referralWidgetId && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Select a widget to generate referral links for your customers
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Reward Configuration */}
          <div className="border border-border rounded-lg bg-muted/20">
            <div className="px-6 py-4 border-b border-border">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Reward Settings
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Configure the reward given to customers who refer new clients
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Reward Type */}
              <div className="space-y-2">
                <Label htmlFor="reward-type">Reward Type</Label>
                <Select
                  value={rewardType}
                  onValueChange={setRewardType}
                >
                  <SelectTrigger id="reward-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Account Credit</SelectItem>
                    <SelectItem value="percentage">Percentage Discount</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Discount</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reward Value */}
              <div className="space-y-2">
                <Label htmlFor="reward-value">
                  {rewardType === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {rewardType === 'percentage' ? '%' : '$'}
                  </span>
                  <Input
                    id="reward-value"
                    type="number"
                    min="0"
                    step={rewardType === 'percentage' ? '1' : '0.01'}
                    max={rewardType === 'percentage' ? '100' : undefined}
                    value={rewardValue}
                    onChange={(e) => setRewardValue(e.target.value)}
                    className="pl-8"
                    placeholder={rewardType === 'percentage' ? '10' : '25.00'}
                  />
                </div>
              </div>
            </div>
          </div>

        </>
      )}

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={savingSettings}
          >
            {savingSettings ? (
              <>
                <Spinner className="size-4 mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      )}

      {/* Success Message */}
      {saveSuccess && !hasChanges && (
        <Alert className="bg-green-500/10 border-green-500/30">
          <AlertDescription className="text-green-400">
            Referral settings saved successfully!
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ReferralSettings;
